import React, { useEffect, useMemo, useState } from 'react';
import {
  MdSearch, MdAdd, MdEdit, MdDelete, MdCheckCircle, MdCancel, MdStar,
  MdComputer, MdTv, MdPhone, MdWifi, MdAir, MdChair,
  MdMic, MdVideocam, MdCable, MdOutlet, MdScreenShare,
  MdSlideshow, MdMonitor, MdDevices, MdRouter,
  MdLightbulb, MdTableRestaurant, MdLocalCafe, MdPrint,
  MdDesktopWindows, MdLaptop, MdKeyboard, MdMouse, MdHeadset
} from 'react-icons/md';
import PageHeader from '../components/PageHeader';
import SkeletonRow from '../components/SkeletonRow';
import StatusToggle from '../../../Components/StatusToggle';
import AddAmenityModal from '../components/AddAmenityModal';
import EditAmenityModal from '../components/EditAmenityModal';
import { amenityApi } from '../../../api/adminapi';
import { toast } from 'react-toastify';
import '../Admin.css';
import './BookRoom.css';
import './Amenities.css';

// Icon mapping helper
const iconMap = {
  MdComputer, MdTv, MdPhone, MdWifi, MdAir, MdChair,
  MdMic, MdVideocam, MdCable, MdOutlet, MdScreenShare,
  MdSlideshow, MdMonitor, MdDevices, MdRouter,
  MdLightbulb, MdTableRestaurant, MdLocalCafe, MdPrint,
  MdDesktopWindows, MdLaptop, MdKeyboard, MdMouse, MdHeadset
};

const getIconComponent = (iconName) => {
  const IconComponent = iconMap[iconName];
  return IconComponent ? <IconComponent /> : iconName || '-';
};

function Amenities() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAmenity, setSelectedAmenity] = useState(null);
  const [list, setList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isTableLoading, setIsTableLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    void loadList();
  }, []);

  const loadList = async () => {
    try {
      setIsTableLoading(true);
      setError('');
      const data = await amenityApi.getAll();
      const arr = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
      setList(arr);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to fetch amenities');
      toast.error('Failed to load amenities', {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsTableLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    return list.filter((amenity) => {
      const name = (amenity.name || '').toLowerCase();
      const description = (amenity.description || '').toLowerCase();
      return !q || name.includes(q) || description.includes(q);
    });
  }, [list, searchQuery]);

  const handleStatusToggle = async (row, nextIsActive) => {
    try {
      const newStatus = nextIsActive ? 'ACTIVE' : 'INACTIVE';
      await amenityApi.updateStatus(row.id, newStatus);
      toast.success('Amenity status updated successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
      await loadList();
    } catch (e) {
      const errorMsg = e.response?.data?.message || e.message || 'Failed to update status';
      setError(errorMsg);
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleEdit = (amenity) => {
    setSelectedAmenity(amenity);
    setShowEditModal(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Are you sure you want to delete "${row.name}"?`)) {
      return;
    }

    try {
      await amenityApi.delete(row.id);
      toast.success('Amenity deleted successfully!', {
        position: "top-right",
        autoClose: 3000,
      });
      await loadList();
    } catch (e) {
      const errorMsg = e.response?.data?.message || e.message || 'Failed to delete amenity';
      setError(errorMsg);
      toast.error(errorMsg, {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  return (
    <div className="admin-view amenities-view">
      <PageHeader breadcrumbs={["Community", "Amenities"]} />

      {/* Compact Header */}
      <div className="page-header-compact">
        <div className="header-left-compact">
          <h2 className="page-title-compact">
            <MdStar className="title-icon" />
            Amenities
          </h2>
        </div>

        <div className="header-filters-compact">
          <div className="input search-compact">
            <span className="icon"><MdSearch /></span>
            <input
              placeholder="Search amenities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <button
            className="btn-book-compact"
            onClick={() => setShowAddModal(true)}
          >
            <MdAdd />
            <span>Add Amenity</span>
          </button>
        </div>
      </div>

      {/* Amenities Table */}
      <div className="table card">
        <div className="table-head">
          <div className="cell">Icon</div>
          <div className="cell">Amenity Name</div>
          <div className="cell">Description</div>
          <div className="cell">Created By</div>
          <div className="cell">Created Date</div>
          <div className="cell">Status</div>
          <div className="cell text-right">Actions</div>
        </div>
        {isTableLoading && Array.from({ length: 6 }).map((_, idx) => (
          <SkeletonRow key={idx} />
        ))}

        {!isTableLoading && filtered && filtered.length > 0 && filtered.map((amenity, idx) => (
          <div className="table-row" key={amenity.id || idx}>
            <div className="cell amenity-icon-cell">{getIconComponent(amenity.icon)}</div>
            <div className="cell"><strong>{amenity.name || ''}</strong></div>
            <div className="cell">{amenity.description || '-'}</div>
            <div className="cell">{amenity.createdBy || '-'}</div>
            <div className="cell">
              {amenity.createdDate ? new Date(amenity.createdDate).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}
            </div>
            <div className="cell">
              <StatusToggle
                id={`amenity-status-${amenity.id}`}
                isActive={(amenity.status || 'ACTIVE') === 'ACTIVE'}
                onToggle={(next) => handleStatusToggle(amenity, next)}
              />
            </div>
            <div className="cell actions-cell">
              <button
                className="btn-action-compact btn-edit"
                onClick={() => handleEdit(amenity)}
                title="Edit amenity"
              >
                <MdEdit />
              </button>
              <button
                className="btn-action-compact btn-delete"
                onClick={() => handleDelete(amenity)}
                title="Delete amenity"
              >
                <MdDelete />
              </button>
            </div>
          </div>
        ))}

        {!isTableLoading && (!filtered || filtered.length === 0) && (
          <div className="table-row">
            <div className="cell no-results-cell">
              {searchQuery ? (
                <p>No amenities match your search criteria</p>
              ) : (
                <p>No amenities found. Click "Add Amenity" to create one.</p>
              )}
            </div>
          </div>
        )}
      </div>

      <AddAmenityModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={loadList}
      />

      <EditAmenityModal
        isOpen={showEditModal}
        amenity={selectedAmenity}
        onClose={() => {
          setShowEditModal(false);
          setSelectedAmenity(null);
        }}
        onSuccess={loadList}
      />
    </div>
  );
}

export default Amenities;
